/**
 * useVoiceDTMFSession hook
 *
 * Manages Voice DTMF session state and data fetching
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  VoiceDTMFSessionState,
  VoiceDTMFScreen,
  MockDTMFScenario,
  SavedCard,
} from '@/types/voice-dtmf.types';
import { logger } from '@utils/logger';
import type { AgentApiClient } from '@services/agent/AgentApiClient';

interface UseVoiceDTMFSessionProps {
  sessionId: string;
  customerId: string;
  amount: number;
  currency: string;
  callReference?: string;
  paymentId?: string; // Kept for API compatibility; resolved from x-session-id header by backend
  useMockData?: boolean;
  mockScenario?: MockDTMFScenario;
  mockAutoProgress?: boolean;
  mockPlaygroundConfig?: {
    withSavedCards?: boolean;
    savedCardsCount?: number;
    includeExpiredCard?: boolean;
  };
  onError?: (error: Error) => void;
  apiClient?: AgentApiClient | null;
  initialManualModeEnabled?: boolean;
  partnerModeEnabled?: boolean;
  interactionType?: 'VOICE' | 'VOICE_MANUAL' | 'CHAT';
}

export const useVoiceDTMFSession = ({
  sessionId,
  customerId,
  amount,
  currency,
  callReference,
  useMockData = false,
  mockScenario = 'new-card-success',
  mockAutoProgress = true,
  mockPlaygroundConfig,
  onError,
  apiClient,
  initialManualModeEnabled = false,
  partnerModeEnabled = false,
  interactionType,
}: UseVoiceDTMFSessionProps) => {
  // VOICE_MANUAL and partner mode both start with secure mode OFF
  const initialSecureModeOff =
    partnerModeEnabled || interactionType === 'VOICE_MANUAL';

  // Initial state - screen will be determined after fetching saved cards
  const [sessionState, setSessionState] = useState<VoiceDTMFSessionState>({
    screen: 'payment-method-selection', // Default, will be updated after fetch
    secureMode: {
      mode: initialSecureModeOff ? 'off' : 'on',
    },
    manualModeAvailable: initialManualModeEnabled,
  });

  const [loading, setLoading] = useState(true);
  const mockDataRef = useRef<{
    steps: Array<{ state: Partial<VoiceDTMFSessionState>; delay: number }>;
    initialState: VoiceDTMFSessionState;
  } | null>(null);
  const mockFlowIdRef = useRef(0);

  // Steps that are waiting for a specific screen before their timers start
  const pendingScreenStepsRef = useRef<{
    waitForScreen: VoiceDTMFScreen;
    steps: Array<{
      delay: number;
      state: Partial<VoiceDTMFSessionState>;
      type?: 'user-action' | 'system-event';
    }>;
    flowId: number;
  } | null>(null);

  const cancelMockTimers = useCallback(() => {
    mockFlowIdRef.current += 1;
    pendingScreenStepsRef.current = null;
  }, []);

  const fetchSessionData = useCallback(
    async (scenarioOverride?: MockDTMFScenario) => {
      try {
        setLoading(true);

        if (useMockData) {
          // Load mock flow definition
          const { getFlowDefinition } =
            await import('../../../mocks/mockVoiceDTMFData');
          const flowDef = getFlowDefinition(
            scenarioOverride ?? mockScenario,
            sessionId,
            customerId,
            amount,
            currency,
            mockPlaygroundConfig
          );

          // Set initial state — preserve attemptCount so Try Again correctly escalates to max-attempts
          setSessionState((prev) => ({
            ...flowDef.initialState,
            attemptCount: prev.attemptCount,
          }));

          // Store mock data for manual simulation
          mockDataRef.current = flowDef;

          // Increment flow ID — any timers from a previous flow will self-cancel
          const flowId = ++mockFlowIdRef.current;

          // Execute flow steps progressively:
          // - system-event steps (or steps with no type): always fire via setTimeout
          //   (simulate backend SSE events such as secure mode activation, DTMF digits)
          // - user-action steps: only fire when mockAutoProgress=true
          // - steps with waitForScreen: deferred until the session screen matches
          if (flowDef.steps.length > 0) {
            // Split at the first step that has waitForScreen
            const waitIndex = flowDef.steps.findIndex((s) => s.waitForScreen);
            const immediateSteps =
              waitIndex === -1
                ? flowDef.steps
                : flowDef.steps.slice(0, waitIndex);
            const deferredSteps =
              waitIndex === -1 ? [] : flowDef.steps.slice(waitIndex);

            const scheduleSteps = (
              steps: typeof flowDef.steps,
              baseDelay = 0
            ) => {
              let accumulatedDelay = baseDelay;
              steps.forEach((step) => {
                accumulatedDelay += step.delay;
                const isSystemEvent = step.type !== 'user-action';
                if (isSystemEvent || mockAutoProgress) {
                  setTimeout(() => {
                    if (mockFlowIdRef.current !== flowId) return;
                    setSessionState((prev) => ({
                      ...prev,
                      ...step.state,
                      cardEntry: step.state.cardEntry
                        ? {
                            ...prev.cardEntry,
                            ...step.state.cardEntry,
                            cardholderName:
                              prev.cardEntry?.cardholderName ||
                              step.state.cardEntry.cardholderName ||
                              '',
                            expiryDate:
                              prev.cardEntry?.expiryDate ||
                              step.state.cardEntry.expiryDate ||
                              '',
                          }
                        : prev.cardEntry,
                    }));
                  }, accumulatedDelay);
                }
              });
            };

            scheduleSteps(immediateSteps);

            if (deferredSteps.length > 0) {
              pendingScreenStepsRef.current = {
                waitForScreen: deferredSteps[0].waitForScreen!,
                steps: deferredSteps,
                flowId,
              };
            }
          }
        } else {
          // Real mode: Fetch saved cards to determine initial screen.
          // VOICE_MANUAL skips this fetch entirely — saved cards are irrelevant in
          // this flow, and skipping eliminates the race condition where VOICE_MANUAL_INITIATED
          // SSE arrives before getSavedCards() resolves and then gets overwritten.
          let savedCardsUI: SavedCard[] = [];
          if (interactionType !== 'VOICE_MANUAL') {
            try {
              if (!apiClient) throw new Error('apiClient not available');
              savedCardsUI = await apiClient.getSavedCards();
            } catch (err) {
              // Non-fatal: proceed without saved cards
              logger.warn(
                'Failed to fetch saved cards, proceeding without saved cards',
                { err }
              );
            }
          }

          // Step 4: Determine initial screen.
          // VOICE_MANUAL sessions start directly on waiting-manual-payment — the checkout
          // URL is opened by the VOICE_MANUAL_INITIATED SSE event.
          const initialScreen =
            interactionType === 'VOICE_MANUAL'
              ? 'waiting-manual-payment'
              : savedCardsUI.length > 0
                ? 'payment-method-selection'
                : 'card-entry';

          logger.info('Session initialised', {
            interactionType,
            savedCardsCount: savedCardsUI.length,
            initialScreen,
          });

          // Step 5: Set initial state using a functional update to preserve any SSE-driven
          // fields (e.g. popupBlockedUrl/popupBlocked from VOICE_MANUAL_INITIATED) that may
          // have arrived before this async fetch completed.
          setSessionState((prev) => ({
            ...prev,
            screen: initialScreen,
            secureMode: {
              mode: initialSecureModeOff ? 'off' : 'on',
              callCode:
                prev.secureMode.callCode ??
                (initialSecureModeOff ? undefined : callReference),
            },
            manualModeAvailable: initialManualModeEnabled,
            savedCards: savedCardsUI.length > 0 ? savedCardsUI : undefined,
            // If no saved cards, pre-select new-card and initialize card entry
            selectedPaymentMethod:
              savedCardsUI.length === 0 ? 'new-card' : undefined,
            cardEntry:
              savedCardsUI.length === 0
                ? {
                    cardholderName: '',
                    cardNumber: { value: '', isComplete: false },
                    cvv: { value: '', isComplete: false },
                    expiryDate: '',
                    saveCard: false,
                  }
                : undefined,
          }));
        }
      } catch (err) {
        logger.error('Fetch error', err);
        setSessionState((prev) => ({
          ...prev,
          screen: 'error',
          errorType: 'unexpected',
          errorMessage:
            err instanceof Error ? err.message : 'Failed to load session',
        }));
        onError?.(
          err instanceof Error ? err : new Error('Failed to load session')
        );
      } finally {
        setLoading(false);
      }
    },
    [
      sessionId,
      customerId,
      amount,
      currency,
      callReference,
      useMockData,
      mockScenario,
      mockAutoProgress,
      mockPlaygroundConfig,
      onError,
      apiClient,
      initialManualModeEnabled,
      initialSecureModeOff,
      interactionType,
    ]
  );

  const hasInitialized = useRef(false);

  useEffect(() => {
    // In real mode, wait for apiClient to be ready before fetching — it may
    // be null on the first render while useAgentApi initialises asynchronously.
    // Without this guard, hasInitialized would be set to true on the null render
    // and the fetch would never retry once apiClient becomes available.
    if (!useMockData && !apiClient) return;
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchSessionData();
    }
  }, [fetchSessionData, useMockData, apiClient]);

  // Fire deferred steps once the session screen reaches the expected screen
  useEffect(() => {
    const pending = pendingScreenStepsRef.current;
    if (!pending) return;
    if (sessionState.screen !== pending.waitForScreen) return;
    if (mockFlowIdRef.current !== pending.flowId) return;

    // Consume — prevent re-firing on subsequent renders with the same screen
    pendingScreenStepsRef.current = null;

    const flowId = pending.flowId;
    let accumulatedDelay = 0;
    pending.steps.forEach((step) => {
      accumulatedDelay += step.delay;
      const isSystemEvent = step.type !== 'user-action';
      if (isSystemEvent || mockAutoProgress) {
        setTimeout(() => {
          if (mockFlowIdRef.current !== flowId) return;
          setSessionState((prev) => ({
            ...prev,
            ...step.state,
            cardEntry: step.state.cardEntry
              ? {
                  ...prev.cardEntry,
                  ...step.state.cardEntry,
                  cardholderName:
                    prev.cardEntry?.cardholderName ||
                    step.state.cardEntry.cardholderName ||
                    '',
                  expiryDate:
                    prev.cardEntry?.expiryDate ||
                    step.state.cardEntry.expiryDate ||
                    '',
                }
              : prev.cardEntry,
          }));
        }, accumulatedDelay);
      }
    });
  }, [sessionState.screen, mockAutoProgress]);

  return {
    sessionState,
    setSessionState,
    loading,
    mockDataRef,
    cancelMockTimers,
    refetch: fetchSessionData, // accepts optional scenarioOverride
  };
};
